import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';

import { useL10n } from '@uplift/hooks';
import { useTimezone } from '@uplift/contexts';
import { formatUserFullName } from '@uplift/utils';
import { Match, PublicUserProfile, User } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';

import { ProviderAvatar } from '../../../components/provider-card/provider-card-avatar.component';
import { useStyles } from './archived-provider.styles';
import { useAssetQuery } from '@uplift/queries';


export type ProviderCardProps = {
  match: Match;
  provider: User;
  providerProfile: PublicUserProfile;
};


export const ArchivedProviderCard: React.FC<ProviderCardProps> = ({ provider, providerProfile }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const userName = formatUserFullName({ fallback: provider.email, firstName: provider.firstName, lastName: provider.lastName, preferredFirstName: providerProfile.preferredName });
  const { data: avatarAsset } = useAssetQuery(providerProfile.avatarAssetId);
  const providerType = provider.medicalRole || ProviderMedicalRole.therapist;

  const lastSeen = React.useMemo<string | undefined>(() => {
    if (provider.lastLogin) {
      return CalendarDateService
        .date(provider.lastLogin)
        .tz(timezone)
        .format(DateFormats['MMM D, YYYY']);
    }
    return undefined;
  }, [ provider.lastLogin, timezone ]);

  return (
    <Card square>
      <CardContent className={classes.content}>
        <Box className={classes.info}>
          <ProviderAvatar src={avatarAsset?.url} />
          <Box className={classes.rightBlock}>
            <Typography color="textSecondary" className={classes.fullNameText}>{userName}</Typography>
            <Typography color="textSecondary" className={classes.typeText}>{l10n.resources.provider.types[providerType]}</Typography>
            {lastSeen && (
              <Typography color="textSecondary" className={classes.lastSeenText}>
                {l10n.yourCareTeam.archivedProviders.lastSeen} {lastSeen}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
